import { useTier, useResetInterval } from '@/store/userStore';
import { CheckOutlined, CloseOutlined, RocketOutlined, Loading3QuartersOutlined, CalendarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Spin, Modal, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { MdDiamond } from 'react-icons/md';
import PaymentModal from './components/PaymentModal';
import axios from 'axios';
import { RiMedal2Fill } from "react-icons/ri";
import { TfiMedall } from "react-icons/tfi";

// Cleaned upgrade plan screen
const FEATURE_PRIORITY: Record<string, number> = {
  'data merging': 1,
  'data cleaning': 2,
  'build dashboard end-to-end': 3,
  'export to dashboard': 4,
  'export to ppt': 5,
  'download insights report': 6,
  'shared dashboard links': 7,
  'update dashboard': 8,
  'external datasource integration': 9,
};

const FEATURE_DESCRIPTIONS: Record<string, string> = {
  'data merging': 'Combine data from multiple files or sources into a single, unified dataset ready for analysis.',
  'data cleaning': 'Automatically detect and fix duplicates, missing values, and formatting inconsistencies before analysis.',
  'build dashboard end-to-end': 'We build a complete, ready-to-use dashboard for you — from raw data to finished visuals.',
  'export to dashboard': 'Export your dashboard to a live, interactive view you can revisit and interact with anytime.',
  'export to ppt': 'Turn your dashboard and insights into a polished, presentation-ready PowerPoint deck.',
  'download insights report': 'Get a written summary of key findings and trends, downloadable as a PDF.',
  'shared dashboard links': 'Generate a link to share your dashboard with teammates or clients — no login required for viewers.',
  'update dashboard': 'Your dashboard automatically pulls in the latest data on a recurring schedule, so it\'s never stale.',
  'external datasource integration': 'Connect directly to external platforms — CRMs, databases, APIs — as live, ongoing data sources.'
};

const getFeaturePriority = (featureName: string) => {
  const key = (featureName || '').trim().toLowerCase();
  return FEATURE_PRIORITY[key] ?? 999;
};

const getFeatureDescription = (featureName: string) => {
  const key = (featureName || '').trim().toLowerCase();
  return FEATURE_DESCRIPTIONS[key] || '';
};

const NewPlanUpgradeScreen = () => {
  const tier = useTier();
  const resetInterval = useResetInterval();
  const { VITE_APP_URL: URL } = import.meta.env;

  const [billCycle, setBillCycle] = useState("monthly");

  const [paymentModalOpen, setPaymentModalOpen] = useState<boolean>(false);
  const [isComingSoonModalOpen, setIsComingSoonModalOpen] = useState<boolean>(false);
  const [comingSoonPlanName, setComingSoonPlanName] = useState<string>("");
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

  const [plans, setPlans] = useState<any[]>([]);
  const [loadingPlans, setLoadingPlans] = useState<boolean>(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${URL}/plans`);
        setPlans(response.data);
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setLoadingPlans(false);
      }
    };

    if (URL) {
      fetchPlans();
    }
  }, [URL]);

  const [myrPrices] = useState<Record<number, { monthly: number; annual: number }>>({});
  const [loadingPlanId] = useState<number | null>(null);

  const handleUpgrade = (planId: number) => {
    const plan = plans.find(p => p.tier_id === planId);
    if (!plan) return;
    
    setSelectedPlanId(planId);
    setPaymentModalOpen(true);
  };

  const handleSegmentPlan = (value: string) => {
    setBillCycle(value);
  }

  const selectedPlanData = plans.find(p => p.tier_id === selectedPlanId);
  const paymentAmount = selectedPlanData ? selectedPlanData.monthly_subscription_per_month_price * 100 : 0;
  const paymentAnnualAmount = selectedPlanData ? selectedPlanData.annual_subscription_per_month_price * 100 : 0;
  const paymentSavings = selectedPlanData && selectedPlanData.monthly_subscription_per_month_price > 0 ? Math.round(((selectedPlanData.monthly_subscription_per_month_price * 12 - selectedPlanData.annual_subscription_per_month_price) / (selectedPlanData.monthly_subscription_per_month_price * 12)) * 100) : 0;
  const paymentMyrMonthly = selectedPlanData ? myrPrices[selectedPlanData.tier_id]?.monthly || 0 : 0;
  const paymentMyrAnnual = selectedPlanData ? myrPrices[selectedPlanData.tier_id]?.annual || 0 : 0;
  const paymentPriceStr = selectedPlanData ? `$${selectedPlanData.monthly_subscription_per_month_price}` : "$0";

  const currentPlan = plans.find(p => p.plan.toLowerCase() === tier);
  const currentTierId = currentPlan ? currentPlan.tier_id : 0;

  return (
    <div className='flex flex-col w-full h-full items-center justify-start gap-y-6 bg-[#F8FAFC] dark:bg-[#020617] pt-10 pb-20 overflow-y-auto overflow-x-hidden'>

      {/* Payment Modal */}
      {selectedPlanData && (
        <PaymentModal
          open={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
          }}
          tier={selectedPlanData.plan.toLowerCase()}
          price={paymentPriceStr}
          tier_id={selectedPlanData.tier_id}
          monthlyCredits={selectedPlanData ? selectedPlanData.plan_alloted_credits.monthly : 0}
          annualCredits={selectedPlanData ? selectedPlanData.plan_alloted_credits.annual : 0}
          billingCycle={billCycle}
          amount={paymentAmount}
          annualAmount={paymentAnnualAmount}
          savings={paymentSavings || 0}
          myrMonthly={paymentMyrMonthly}
          myrAnnual={paymentMyrAnnual}
          stripePriceMonthlyId={selectedPlanData?.stripe_price_monthly_id}
          stripePriceAnnualId={selectedPlanData?.stripe_price_annual_id}
          stripeProductID={selectedPlanData?.stripe_product_id}
        />
      )}

      {/* Coming Soon Modal */}
      <Modal
        open={isComingSoonModalOpen}
        onCancel={() => setIsComingSoonModalOpen(false)}
        footer={null}
        centered
        width={400}
        closeIcon={null}
      >
        <div className="flex flex-col items-center justify-center pt-4 pb-2 text-center">
          {/* Icon */}
          <div className="relative mb-4">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 24H52" stroke="#C41230" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 40H24" stroke="#C41230" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 14C12 12.8954 12.8954 12 14 12H50C51.1046 12 52 12.8954 52 14V46C52 47.1046 51.1046 48 50 48H14C12.8954 48 12 47.1046 12 46V14Z" stroke="#C41230" strokeWidth="3" strokeLinecap="round"/>
              <rect x="36" y="36" width="16" height="12" rx="2" stroke="#C41230" strokeWidth="3" fill="white"/>
              <path d="M40 36V32C40 29.7909 41.7909 28 44 28C46.2091 28 48 29.7909 48 32V36" stroke="#C41230" strokeWidth="3"/>
              {/* Stars decoration */}
              <circle cx="56" cy="16" r="1.5" fill="#C41230"/>
              <circle cx="14" cy="8" r="1.5" fill="#C41230"/>
              <circle cx="8" cy="30" r="1.5" fill="#C41230"/>
              <path d="M58 28L60 30L58 32L56 30L58 28Z" fill="#C41230"/>
            </svg>
          </div>
          
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Payment Gateway integration work is in progress</h2>
          <p className="text-gray-700 dark:text-gray-600 text-[13px] leading-relaxed mb-6 px-4">
            Online payments are launching soon. We'll notify you the moment your selected plan — {comingSoonPlanName} — is ready for checkout.
          </p>
          
          <div className="flex items-center gap-2 px-5 py-1.5 bg-[#fcebee] dark:bg-[#C41230]/20 text-[#C41230] dark:text-[#ef4444] rounded-full mb-6 text-[13px] font-semibold">
            <CalendarOutlined />
            <span>Coming Soon</span>
          </div>
          
          <Button 
            type="primary" 
            onClick={() => setIsComingSoonModalOpen(false)}
            style={{
              backgroundColor: '#C41230',
              borderColor: '#C41230',
              height: 42,
              width: '90%',
              borderRadius: 8,
              fontWeight: 600
            }}
          >
            Got it
          </Button>
        </div>
      </Modal>

      {/* Title Section */}
      <div className='flex flex-col gap-3 items-center'>
          <h1 className='font-bold text-5xl dark:text-white'>Plans that grow with your data</h1>
          <h2 className='text-[16px] text-gray-600 dark:text-gray-400'>Choose the plan that fits how you work. Switch anytime.</h2>
      </div>

      <div className='flex items-center bg-[#F1F5F9] dark:bg-slate-800 rounded-full p-1.5 mt-4 mb-2'>
        <button 
          className={`px-8 py-2 rounded-full cursor-pointer transition-all duration-300 flex items-center justify-center ${
            billCycle === 'monthly' 
              ? 'bg-white dark:bg-slate-700 shadow-sm font-bold text-[#1e293b] dark:text-white' 
              : 'text-[#64748b] dark:text-gray-400 font-bold hover:text-[#475569] dark:hover:text-gray-300'
          }`}
          onClick={() => handleSegmentPlan('monthly')}
        >
          <span className="text-[13px] tracking-wide">Monthly</span>
        </button>
        <button 
          className={`flex items-center gap-3 px-6 py-2 rounded-full cursor-pointer transition-all duration-300 ${
            billCycle === 'yearly' 
              ? 'bg-white dark:bg-slate-700 shadow-sm font-bold text-[#1e293b] dark:text-white' 
              : 'text-[#64748b] dark:text-gray-400 font-bold hover:text-[#475569] dark:hover:text-gray-300'
          }`}
          onClick={() => handleSegmentPlan('yearly')}
        >
          <span className="text-[13px] tracking-wide">Annually</span>
          <span className="bg-[#fcebee] dark:bg-[#C41230]/20 text-[#C41230] dark:text-[#ef4444] text-sm font-bold px-3 py-1 rounded-full">
            Save up to 17%
          </span>
        </button>
      </div>

      {/* Cards */}
      <div className='flex w-full px-8 items-stretch justify-center gap-5 mt-4 flex-wrap xl:flex-nowrap'>
        {loadingPlans ? (
          <div className='w-full flex items-center justify-center mt-20'>
            <Spin size='large' className='text-[#C41230]' indicator={<Loading3QuartersOutlined spin style={{ fontSize: 40 }} />} />
          </div>
        ) : plans.map((plan) => {
          const isEnterprise = plan.plan?.toLowerCase() === 'enterprise';
          const savingsPercentage = plan.monthly_subscription_per_month_price > 0 ? Math.round(((plan.monthly_subscription_per_month_price * 12 - plan.annual_subscription_per_month_price) / (plan.monthly_subscription_per_month_price * 12)) * 100) : 0;
          const isDowngrade = currentTierId > 0 && plan.tier_id < currentTierId;

          const sortedAllowedFeatures = [...(plan.allowed_features || [])].sort(
            (a, b) => getFeaturePriority(a.feature) - getFeaturePriority(b.feature)
          );

          const sortedDisallowedFeatures = [...(plan.disallowed_features || [])]
            .filter(f => f.feature?.trim().toLowerCase() !== 'external datasource integration')
            .sort((a, b) => getFeaturePriority(a.feature) - getFeaturePriority(b.feature));
          
          return (
            <div key={plan.tier_id} className={`p-4 flex-1 max-w-[400px] min-h-[600px] border-[1.5px] rounded-xl flex flex-col gap-y-4 bg-white dark:bg-[#0f172a] shadow-md relative ${plan.isPopular ? "border-[#C41230]" : "border-[#e6e2e2] dark:border-slate-700"}`}>

                {plan.isPopular && (
                    <div className='px-4 py-1 bg-[#C41230] text-white font-bold rounded-full w-fit absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs'>
                      Most Popular
                    </div>
                )}

                {/* Icon Section */}
                <div className='w-full flex items-center justify-between'>
                  <div className='flex items-center justify-center w-[44px] h-[44px] bg-[#fcebee] dark:bg-[#C41230]/20 rounded-xl'>
                    {isEnterprise ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C41230" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                      </svg>
                    ) : plan.tier_id === 1 || plan.plan?.toLowerCase() === 'trial' ? (
                      <RocketOutlined style={{fontSize:"24px",color: "#C41230"}} className='rotate-45'/>
                    ) : plan.tier_id === 2 || plan.plan?.toLowerCase() === 'lite' ? (
                      <MdDiamond style={{fontSize:"24px",color: "#C41230"}}/>
                    ) : plan.tier_id === 3 || plan.plan?.toLowerCase() === 'standard' ? (
                      <TfiMedall style={{fontSize:"24px",color: "#C41230"}} />
                    ) : (
                      <RiMedal2Fill style={{fontSize:"24px",color: "#C41230"}} />
                    )}
                  </div>
                  <div className='flex items-center gap-2'>
                    {tier === plan.plan?.toLowerCase() && (
                      <div className='px-2 py-0.5 rounded-full bg-[#F1F5F9] dark:bg-slate-800 flex items-center justify-center'>
                        <span className='text-[#64748B] dark:text-gray-400 font-bold text-[11px] select-none'>Current</span>
                      </div>
                    )}
                    <div className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold select-none ${
                      isEnterprise
                        ? 'bg-[#C41230] text-white'
                        : 'bg-[#F1F5F9] dark:bg-slate-800 text-[#64748B] dark:text-gray-400'
                    }`}>
                      {isEnterprise ? 'Business Plan' : 'Individual Plan'}
                    </div>
                  </div>
                </div>

                {/* Title with description */}
                <div className='flex flex-col gap-y-2 mt-2'>
                    <h3 className='font-bold text-xl text-black dark:text-white line-clamp-1'>{plan.plan}</h3>
                    <p className='text-gray-600 dark:text-gray-400 text-[13px] h-[75px] xl:h-[90px] overflow-hidden leading-relaxed'>
                      {plan.plan_description}
                    </p>
                </div>

                {/* Price with credits Section */}
                {!isEnterprise ? (
                  <div className='h-[110px] flex flex-col justify-between'>
                    <div className='w-full flex items-center justify-between'>
                      <div className='flex flex-col gap-y-1 mt-2'>
                        <span className='font-semibold text-lg text-gray-600 dark:text-gray-400'>
                          {plan.plan?.toLowerCase() === 'trial' ? (
                            <b className='text-4xl font-bold text-black dark:text-white'>$0</b>
                          ) : (
                            <>
                              <b className='text-4xl font-bold text-black dark:text-white'>
                                ${billCycle === "yearly" && plan.annual_subscription_per_month_price > 0 ? (plan.annual_subscription_per_month_price / 12).toFixed(2).replace(/\.00$/, '') : plan.monthly_subscription_per_month_price}
                              </b> <span className='text-sm'>/month</span>
                            </>
                          )}
                        </span>
                        <span className='text-gray-600 dark:text-gray-400 text-[14px] mt-1'>
                          {plan.plan?.toLowerCase() === 'trial' ? (
                            <><b className='font-bold text-black dark:text-white'>30</b> credits</>
                          ) : (
                            <><b className='font-bold text-black dark:text-white'>{billCycle === "yearly" ? plan.plan_alloted_credits?.annual : plan.plan_alloted_credits?.monthly}</b> credits {billCycle === 'yearly' ? 'annually' : 'monthly'}</>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Savings Tag */}
                    <div className='h-[30px] flex items-center'>
                      {billCycle === "yearly" && plan.annual_subscription_per_month_price > 0 && (
                        <div className='px-3 py-1 flex items-center gap-2 bg-[#fcebee] dark:bg-[#C41230]/20 rounded-full w-fit'>
                            <span className='font-semibold text-[#C41230] dark:text-[#ef4444] text-[11px] whitespace-nowrap'>Billed ${plan.annual_subscription_per_month_price} annually (Save {savingsPercentage}%)</span>
                        </div> 
                      )}
                    </div>
                  </div>
                ) : (
                  <div className='h-[110px] flex flex-col justify-end'></div>
                )}

                {/* Button */}
                {(() => {
                  if (isEnterprise) {
                    return (
                      <Button
                        type='primary'
                        style={{
                          height: 40,
                          fontWeight: "bold",
                          marginTop: "10px",
                          borderRadius: "10px",
                          backgroundColor: '#C41230',
                          borderColor: '#C41230'
                        }}
                        onClick={() => {
                          window.open('https://www.valiantlytix.com/contact.html', '_blank');
                        }}
                      >
                        Contact Us
                      </Button>
                    );
                  }

                  const isCurrentTier = tier === plan.plan?.toLowerCase();
                  
                  const userInterval = resetInterval?.toLowerCase() || '';
                  const isAnnualUser = ['annual', 'year', 'yearly'].includes(userInterval);
                  const isMonthlyUser = ['monthly', 'month'].includes(userInterval);

                  const isCurrentCycle = (isAnnualUser && billCycle === 'yearly') || 
                                         (isMonthlyUser && billCycle === 'monthly') || 
                                         (!isAnnualUser && !isMonthlyUser);

                  const isExactlyCurrent = isCurrentTier && isCurrentCycle;

                  if (isExactlyCurrent) {
                    return <Button style={{height:40,fontWeight:"bold",marginTop:"10px",borderRadius:"10px"}}>Current Plan</Button>;
                  }
                  
                  if (plan.monthly_subscription_per_month_price === 0) {
                    return <Button type='primary' disabled={isDowngrade} style={{height:40,fontWeight:"bold",marginTop:"10px",borderRadius:"10px",opacity:isDowngrade ? 0.5 : 1}}>Start Free Trial</Button>;
                  }
                  
                  const isCycleDowngrade = isCurrentTier && isAnnualUser && billCycle === 'monthly';
                  const shouldDisable = isDowngrade || isCycleDowngrade;
                  const buttonText = isCycleDowngrade ? "Downgrade unavailable" : (isCurrentTier ? `Upgrade to Annually` : `Get ${plan.plan}`);

                  return (
                    <Button type='primary' disabled={shouldDisable} loading={loadingPlanId === plan.tier_id} style={{height:40,fontWeight:"bold",marginTop:"10px",borderRadius:"10px",opacity:shouldDisable ? 0.5 : 1}} onClick={() => {
                      handleUpgrade(plan.tier_id)
                    }}>{buttonText}</Button>
                  );
                })()}

                <div className='w-full h-[1px] bg-gray-300 dark:bg-slate-700 mt-2 shrink-0'/>

                {/* Whats Included Section */}
                <div className='flex flex-col gap-y-4 mt-2 w-full items-center flex-grow'>
                    <div className='flex flex-col gap-y-3 w-[100%]'>
                      <h2 className='font-bold text-[13px] mb-2 text-[#334155] dark:text-slate-300'>What's included:</h2>

                       {sortedAllowedFeatures.map((feature: any, idx: number) => {
                              const desc = getFeatureDescription(feature.feature);
                              const words = (feature.feature || '').trim().split(' ');
                              const lastWord = words.pop();
                              const rest = words.join(' ');

                              return (
                                <div key={`allowed-${idx}`} className='flex items-start gap-2 text-[12px] text-black dark:text-white font-medium'>
                                    <div className="mt-[3px] flex-shrink-0">
                                      <CheckOutlined style={{fontSize:"12px",color:"#C41230"}}/>
                                    </div>
                                    <div className='leading-tight flex-1'>
                                      {rest}{rest ? ' ' : ''}
                                      <span className="whitespace-nowrap">
                                        {lastWord}
                                        {desc && (
                                          <>
                                            &nbsp;
                                            <Tooltip title={desc} placement="top" overlayInnerStyle={{ borderRadius: '6px', padding: '6px 10px', fontSize: '12px' }}>
                                              <InfoCircleOutlined className="text-gray-400 hover:text-[#C41230] cursor-help transition-colors relative -top-[1px]" />
                                            </Tooltip>
                                          </>
                                        )}
                                      </span>
                                    </div>
                                </div>
                              );
                          })}

                          {sortedDisallowedFeatures.map((feature: any, idx: number) => {
                              const desc = getFeatureDescription(feature.feature);
                              const words = (feature.feature || '').trim().split(' ');
                              const lastWord = words.pop();
                              const rest = words.join(' ');

                              return (
                                <div key={`disallowed-${idx}`} className='flex items-start gap-2 text-[12px] text-gray-400 dark:text-gray-500 font-medium'>
                                    <div className="mt-[3px] flex-shrink-0">
                                      <CloseOutlined style={{fontSize:"12px"}}/>
                                    </div>
                                    <div className='leading-tight flex-1'>
                                      {rest}{rest ? ' ' : ''}
                                      <span className="whitespace-nowrap">
                                        {lastWord}
                                        {desc && (
                                          <>
                                            &nbsp;
                                            <Tooltip title={desc} placement="top" overlayInnerStyle={{ borderRadius: '6px', padding: '6px 10px', fontSize: '12px' }}>
                                              <InfoCircleOutlined className="text-gray-300 hover:text-gray-500 cursor-help transition-colors relative -top-[1px]" />
                                            </Tooltip>
                                          </>
                                        )}
                                      </span>
                                    </div>
                                </div>
                              );
                          })}
                    </div>
                </div>

            </div>
          )
        })}
      </div>

    </div>
  )
}

export default NewPlanUpgradeScreen;